import PromotionItemI = Models.PromotionItemI
import PromotionRuleItemsI = Models.PromotionRuleItemsI
import { TargetingType } from '../config'
import Promotion = Models.Promotion
import PromotionRuleI = Models.PromotionRuleI

export const DEFAULT_PROMOTION_RULE_OPERATORS: Pick<
    PromotionRuleI,
    | 'orders_operator'
    | 'adjusted_orders_operator'
    | 'spend_operator'
    | 'acos_operator'
    | 'blended_acos_operator'
    | 'clicks_operator'
    | 'adjusted_pages_read_operator'
    | 'adjusted_estimated_royalties_operator'
    | 'blended_profit_operator'
> = {
    orders_operator: '>',
    adjusted_orders_operator: '>',
    spend_operator: '>',
    acos_operator: '<',
    blended_acos_operator: '<',
    clicks_operator: '>',
    adjusted_pages_read_operator: '>',
    adjusted_estimated_royalties_operator: '>',
    blended_profit_operator: '>',
}

export const normalizePromotionRuleOperators = <T extends PromotionRuleI>(rule: T): T => {
    const next = { ...rule }
    Object.entries(DEFAULT_PROMOTION_RULE_OPERATORS).forEach(([key, defaultValue]) => {
        const typedKey = key as keyof typeof DEFAULT_PROMOTION_RULE_OPERATORS
        if (next[typedKey] === null || next[typedKey] === undefined || next[typedKey] === '') {
            next[typedKey] = defaultValue
        }
    })
    return next
}

/**
 * Attempts to order the ad groups in the following way:
 *      1. Auto Campaigns
 *      2. Manual Campaigns
 *          a. Testing Ad Groups
 *              i. Broad
 *              ii. Phrase
 *              iii. Exact
 *          b. Performance Ad Groups
 *              i. Broad
 *              ii. Phrase
 *              iii. Exact
 *      3. Others
 *
 * Whether or not the campaign is auto / manual is determined by the
 * linked campaign's targeting_type.
 *
 * Whether or not an ad group is a testing or performance ad group, as well as
 * it's targeting type is determined by looking for different strings in the
 * campaign or ad group names.
 *
 * Unmatched items will be put at the end. These would be items where the
 * ad group tier or keyword targeting type can be inferred from the name.
 *
 * So a campaign / ad group of "Campaign 1", "Ad Group 1" would be put at
 * the end, since we can't deduce it's ad group tier or targeting type.
 *
 * @param {PromotionRuleItemsI[]} items
 * @return {PromotionRuleItemsI[]}
 */
export const groupItems = <T extends PromotionItemI | PromotionRuleItemsI>(items: T[]): T[] => {
    const sortAlphabetically = (
        a: PromotionItemI | PromotionRuleItemsI,
        b: PromotionItemI | PromotionRuleItemsI,
    ): number => {
        const aCampaignName = a.campaign?.name.toLowerCase() || a.campaign_name?.toLowerCase()
        const aAdGroupName = a.ad_group?.name.toLowerCase() || a.campaign_name?.toLowerCase()
        const bCampaignName = b.campaign?.name.toLowerCase() || b.campaign_name?.toLowerCase()
        const bAdGroupName = b.ad_group?.name.toLowerCase() || b.campaign_name?.toLowerCase()

        if (aCampaignName < bCampaignName) {
            return -1
        } else if (aCampaignName > bCampaignName) {
            return 1
        } else {
            if (aAdGroupName < bAdGroupName) {
                return -1
            } else if (aAdGroupName > bAdGroupName) {
                return 1
            } else {
                return 0
            }
        }
    }

    const autos: any[] = []
    const manualTestingBroad: any[] = []
    const manualTestingPhrase: any[] = []
    const manualTestingExact: any[] = []
    const manualTestingOthers: any[] = []
    const manualPerformanceBroad: any[] = []
    const manualPerformancePhrase: any[] = []
    const manualPerformanceExact: any[] = []
    const manualPerformanceOthers: any[] = []
    const manualOthers: any[] = []
    const others: any[] = []

    items.forEach((item) => {
        const campaignName = item.campaign?.name.toLowerCase() || item.campaign_name?.toLowerCase()
        const adGroupName = item.ad_group?.name.toLowerCase() || item.ad_group_name?.toLowerCase()

        if (
            item.campaign?.targeting_type == TargetingType.Auto ||
            item.campaign_targeting_type == TargetingType.Auto
        ) {
            autos.push(item)
            return
        } else if (
            item.campaign?.targeting_type == TargetingType.Manual ||
            item.campaign_targeting_type == TargetingType.Manual
        ) {
            if (campaignName.includes('testing') || adGroupName.includes('testing')) {
                if (campaignName.includes('broad') || adGroupName.includes('broad')) {
                    manualTestingBroad.push(item)
                    return
                } else if (campaignName.includes('phrase') || adGroupName.includes('phrase')) {
                    manualTestingPhrase.push(item)
                    return
                } else if (campaignName.includes('exact') || adGroupName.includes('exact')) {
                    manualTestingExact.push(item)
                    return
                } else {
                    manualTestingOthers.push(item)
                    return
                }
            } else if (
                campaignName.includes('performance') ||
                adGroupName.includes('performance')
            ) {
                if (campaignName.includes('broad') || adGroupName.includes('broad')) {
                    manualPerformanceBroad.push(item)
                    return
                } else if (campaignName.includes('phrase') || adGroupName.includes('phrase')) {
                    manualPerformancePhrase.push(item)
                    return
                } else if (campaignName.includes('exact') || adGroupName.includes('exact')) {
                    manualPerformanceExact.push(item)
                    return
                } else {
                    manualPerformanceOthers.push(item)
                    return
                }
            } else {
                manualOthers.push(item)
                return
            }
        } else {
            others.push(item)
            return
        }
    })

    autos.sort(sortAlphabetically)
    manualTestingBroad.sort(sortAlphabetically)
    manualTestingPhrase.sort(sortAlphabetically)
    manualTestingExact.sort(sortAlphabetically)
    manualTestingOthers.sort(sortAlphabetically)
    manualPerformanceBroad.sort(sortAlphabetically)
    manualPerformancePhrase.sort(sortAlphabetically)
    manualPerformanceExact.sort(sortAlphabetically)
    manualPerformanceOthers.sort(sortAlphabetically)
    manualOthers.sort(sortAlphabetically)
    others.sort(sortAlphabetically)

    return [
        ...autos,
        ...manualTestingBroad,
        ...manualTestingPhrase,
        ...manualTestingExact,
        ...manualTestingOthers,
        ...manualPerformanceBroad,
        ...manualPerformancePhrase,
        ...manualPerformanceExact,
        ...manualPerformanceOthers,
        ...manualOthers,
        ...others,
    ]
}

export const sortItems = (
    a: PromotionItemI | PromotionRuleItemsI,
    b: PromotionItemI | PromotionRuleItemsI,
) => {
    if (a?.campaign_name === b?.campaign_name) {
        if (a?.ad_group_name > b?.ad_group_name) {
            return 1
        } else if (a?.ad_group_name < b?.ad_group_name) {
            return -1
        } else {
            return 0
        }
    }

    if (a?.campaign_name > b?.campaign_name) {
        return 1
    } else if (a?.campaign_name < b?.campaign_name) {
        return -1
    } else {
        return 0
    }
}

export const NewPromotionValues: PromotionRuleI = {
    id: 0,
    uuid: '',
    promotion_id: 0,
    enabled: true,

    name: 'My Smart Campaign rule',

    orders_value: 2,
    orders_operator: DEFAULT_PROMOTION_RULE_OPERATORS.orders_operator,

    adjusted_orders_value: '',
    adjusted_orders_operator: DEFAULT_PROMOTION_RULE_OPERATORS.adjusted_orders_operator,

    spend_value: '',
    spend_operator: DEFAULT_PROMOTION_RULE_OPERATORS.spend_operator,

    acos_value: '',
    acos_operator: DEFAULT_PROMOTION_RULE_OPERATORS.acos_operator,

    blended_acos_value: '',
    blended_acos_operator: DEFAULT_PROMOTION_RULE_OPERATORS.blended_acos_operator,

    clicks_value: '',
    clicks_operator: DEFAULT_PROMOTION_RULE_OPERATORS.clicks_operator,

    adjusted_pages_read_value: '',
    adjusted_pages_read_operator: DEFAULT_PROMOTION_RULE_OPERATORS.adjusted_pages_read_operator,
    adjusted_estimated_royalties_value: '',
    adjusted_estimated_royalties_operator:
        DEFAULT_PROMOTION_RULE_OPERATORS.adjusted_estimated_royalties_operator,

    blended_profit_value: '',
    blended_profit_operator: DEFAULT_PROMOTION_RULE_OPERATORS.blended_profit_operator,

    lookback_days: '',

    bid_increase_amount: '',
    bid_increase_percent: '',
    bid_set: '',

    rule_items: [],

    created_at: '',
    updated_at: '',
}

/**
 * The dry run opens in a new tab, so it loses the current query string. Carry
 * the query string over and pin `profileId` to the promotion's own profile,
 * otherwise the new tab falls back to the account's default profile.
 */
export const dryRunHref = (
    promotionId: number,
    profileId: number | string | null | undefined,
    currentSearch: string,
): string => {
    const params = new URLSearchParams(currentSearch)

    if (profileId != null && profileId !== '') {
        params.set('profileId', String(profileId))
    }

    const query = params.toString()

    return `/smart-campaigns/${promotionId}/dry-run${query ? `?${query}` : ''}`
}

export const DefaultPromotion: Promotion = {
    id: 0,
    profile_id: 0,
    name: 'My Smart Campaign',
    ad_product: 'SPONSORED_PRODUCTS',
    created_at: '',
    updated_at: '',
    enabled: true,
    items: [],
}
